import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useFeatures } from "flagged";
import {
  folderIcon,
  usersIcon,
  menuAltIcon,
  serverIcon,
  hashTagIcon,
  dbSidePanelIcon,
  serviceIcon,
  endpointIcon,
  reloadIcon,
  appLightIcon,
  deployIcon,
  queryToolIcon,
} from "../../../assets/icons";
import Menubar from "./Menubar";
import {
  fetchApplicationById,
  fetchApplications,
  fetchConstantsFromLibrary,
  fetchApplicationDeploymentStatus,
  selectApplicationFilters,
  selectSelectedApp,
  selectSelectedAppId,
  selectShouldShowLeftPane,
  selectTotalApplications,
  selectAppDeployStatus,
  selectRecentSelectedApp,
  fetchApplicationsDeploymentsTrack,
} from "../../../pages/Applications";
import {
  selectSelectedDataTable,
  selectTotalBackgroundTasks,
  selectTotalCollections,
  fetchDataModels,
  fetchCollection,
  fetchCollections,
  fetchBackgroundTasks,
  fetchDataModel,
  fetchAllRecordsFromTable,
  selectSelectedCollectionId,
  selectCollectionsFilters,
  selectDataModelsFilters,
  selectSelectedDataModel,
  selectSelectedDataModelId,
  selectTotalDataModels,
  selectTotalEndpoints,
  selectTotalServices,
  selectFormattedFilters,
  fetchEndpoints,
  fetchServices,
  fetchService,
  selectSelectedServiceId,
  fetchDataModelRegions,
  fetchSwaggerData,
  selectEndpoints,
  selectBackgroundTaskFilters,
  fetchPipelineStatus,
  selectRecentDeployedEndpointInfo,
  fetchENVSecrets,
  selectEndpointFilters,
  selectServiceFilters,
  fetchDataModelDeploymentsTrack,
  selectDeployStatus,
  selectAreResourcesPending,
} from "../../../pages/DataModels";
import { selectSelectedModal, selectShouldShowDeploymentModal } from "../Modal";
import { selectUser } from "../../../pages/UserProfile";
import {
  fetchInvitations,
  fetchUsers,
  selectInvitationsFilters,
  selectTotalUsers,
  selectUsersFilters,
} from "../../../pages/Users";
import {
  BACKEND_FAILURE_STATUS,
  BACKEND_INITIAL_STATUS,
  DEPLOY_STATUSES,
  EMPTY_ARRAY,
  EMPTY_STRING,
  FRONTEND_FAILURE_STATUS,
  FRONTEND_INITIAL_STATUS,
  INDEX,
  LIBRARY_CONSTANTS,
  CACHE_KEYS,
  APP_DEPLOY_AT,
  REFRESH_FRONTEND_DEPLOY_STATUS,
} from "../../../constants";
import {
  fetchMulesoftEndpoints,
  fetchMuleIntegrations,
  fetchIntegration,
  selectMulesoftEndpoints,
  selectMulesoftServices,
  setMulesoftDeployStatus,
  selectMuleIntegrations,
  selectSelectedIntegrationId,
  fetchMulesoftSwaggerData,
  selectMulesoftDeployStatus,
  selectMuleIntegrationsFilters,
} from "../../../pages/Mulesoft";
import {
  fetchOrganizationUsersById,
  fetchOrganizations,
  selectOrganizationUsersFilters,
  selectOrganizationUsersId,
  selectOrganizationsFilters,
} from "../../../pages/Organizations";
import { getDeploymentStatus } from "../../../helpers";
import { useLocation } from "react-router-dom";
import { actions as modalActions } from "../Modal/modal.reducers";
import DeploymentLogsModal from "../AppModals/DataModelModals/DeploymentLogsModal";
import { actions as appActions } from "../../../pages/Applications/applications.reducers";
import { actions as dataModalActions } from "../../../pages/DataModels/dataModels.reducers";
import renderModal from "../AppModals";
import { selectSelectedDrawer } from "../Drawer";
import renderDrawer from "../AppDrawers";
import { setAlert } from "../Alert";
import { selectSelectedDataModelResources } from "../../../pages/DataModels/dataModels.selectors";

const { setAppDeployStatus, setAppPipelineStatus } = appActions;
const {
  setDeployStatus,
  setPipelineStatus,
  setDataModelResources,
  setDataModelWithPendingResources,
  setAreResourcesPending,
} = dataModalActions;

const { SECOND } = INDEX;
const { ORGANIZATION } = CACHE_KEYS;
const { SUCCESS, FAILURE, REPO_NOT_FOUND, IN_PROGRESS, CREATING_PROJECT } =
  DEPLOY_STATUSES;
const PENDING = "pending";
const INTERVAL_TIME = 10000;

const SidePanel = () => {
  const dispatch = useDispatch();
  const {
    showApplications,
    showUsers,
    showDataModels,
    showApplicationSettings,
    showAppDataModal,
    showCollections,
    showBackgroundTasks,
    showEndpoints,
    showServices,
    showMulesoftGeneratedEndpoints,
    showApiDocs,
    showData,
    showDatamodelSettings,
    showIntegrations,
    showApplicationDeploymentsTrack,
    showDataModelDeploymentsTrack,
    showQueryTool,
  } = useFeatures();

  const location = useLocation();
  const currentUser = useSelector(selectUser);
  const pipelineStatusInterval = useRef();

  const selectedModal = useSelector(selectSelectedModal);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const selectedApp = useSelector(selectSelectedApp);
  const shouldShowLeftPane = useSelector(selectShouldShowLeftPane);
  const selectRecentApp = useSelector(selectRecentSelectedApp);

  const totalApplications = useSelector(selectTotalApplications);
  const totalDataModels = useSelector(selectTotalDataModels);
  const totalUsers = useSelector(selectTotalUsers);

  const totalBackgroundTasks = useSelector(selectTotalBackgroundTasks);
  const totalCollections = useSelector(selectTotalCollections);
  const totalEndpoints = useSelector(selectTotalEndpoints);
  const totalServices = useSelector(selectTotalServices);

  const appId = useSelector(selectSelectedAppId);
  const dataModelId = useSelector(selectSelectedDataModelId);
  const selectedDataTable = useSelector(selectSelectedDataTable);
  const selectedCollectionId = useSelector(selectSelectedCollectionId);
  const selectedServiceId = useSelector(selectSelectedServiceId);
  const endpoints = useSelector(selectEndpoints);
  const integrationId = useSelector(selectSelectedIntegrationId);

  const applicationFilters = useSelector(selectApplicationFilters);
  const dataModelsFilters = useSelector(selectDataModelsFilters);
  const usersFilters = useSelector(selectUsersFilters);
  const invitationFilters = useSelector(selectInvitationsFilters);
  const collectionFilters = useSelector(selectCollectionsFilters);
  const endpointsFilters = useSelector(selectEndpointFilters);
  const servicesFilters = useSelector(selectServiceFilters);
  const tableDataFilters = useSelector(selectFormattedFilters);
  const backgroundTaskFilters = useSelector(selectBackgroundTaskFilters);
  const allMulesoftEndpoints = useSelector(selectMulesoftEndpoints);
  const allMulesoftServices = useSelector(selectMulesoftServices);
  const muleIntegrations = useSelector(selectMuleIntegrations);
  const recentDeployedEndpointInfo = useSelector(
    selectRecentDeployedEndpointInfo
  );
  const muleIntegrationsFilters = useSelector(selectMuleIntegrationsFilters);
  const organizationFilters = useSelector(selectOrganizationsFilters);
  const selectedOrganizationUsersId = useSelector(selectOrganizationUsersId);
  const organizationUsersFilters = useSelector(selectOrganizationUsersFilters);
  const shouldShowDeploymentLogModal = useSelector(
    selectShouldShowDeploymentModal
  );
  const deployStatus = useSelector(selectAppDeployStatus);
  const dataModelDeployStatus = useSelector(selectDeployStatus);
  const integrationDeployStatus = useSelector(selectMulesoftDeployStatus);
  const selectedDrawer = useSelector(selectSelectedDrawer);
  const selectedDataModelResources = useSelector(
    selectSelectedDataModelResources
  );
  const areResourcesPending = useSelector(selectAreResourcesPending);
  const [organizationId, setOrganizationId] = useState(EMPTY_STRING);
  const [applicationsCount, setApplicationsCount] = useState(0);
  const [dataModelsCount, setDataModelsCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [collectionsCount, setCollectionsCount] = useState(0);
  const [backgroundTasksCount, setBackgroundTasksCount] = useState(0);
  const [endpointsCount, setEndpointsCount] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);
  const [totalMulesoftProjects, setTotalMulesoftProjects] = useState(0);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [shouldShowDrawer, setShouldShowDrawer] = useState(false);
  const [isDeployStatusInProgress, setIsDeployStatusInProgress] =
    useState(false);

  const [mulesoftEndpoints, setMulesoftEndpoints] = useState(EMPTY_ARRAY);
  const [currentTab, setCurrentTab] = useState(EMPTY_STRING);
  const { setSelectedModal } = modalActions;
  const appDeploymentTime = JSON.parse(localStorage.getItem(APP_DEPLOY_AT));

  const shouldFetchDeploymentLogs = (status) =>
    [SUCCESS, FAILURE, CREATING_PROJECT, REPO_NOT_FOUND].includes(status) ||
    !status;

  useEffect(() => {
    const organization = JSON.parse(sessionStorage.getItem(ORGANIZATION));
    setOrganizationId(organization?.id);
  }, []);

  useEffect(() => {
    if (!selectedModal)
      window.onpopstate = () => {
        dispatch(setSelectedModal(null));
      };
  }, [selectedModal, dispatch, setSelectedModal]);

  useEffect(() => {
    deployStatus &&
      setIsDeployStatusInProgress(
        deployStatus?.frontend === IN_PROGRESS ||
          deployStatus?.backend === IN_PROGRESS
      );
  }, [deployStatus]);

  useEffect(() => {
    setShouldShowDrawer(selectedDrawer);
  }, [selectedDrawer]);

  useEffect(() => {
    setShouldShowModal(selectedModal);
  }, [selectedModal]);

  useEffect(() => {
    if (!selectedDrawer && !selectedModal) dispatch(setAlert());
  }, [selectedDrawer, selectedModal, dispatch]);

  useEffect(() => {
    const updatedTab = location.pathname.split("/")[SECOND];
    if (currentTab !== updatedTab) setCurrentTab(updatedTab);
  }, [location, currentTab]);

  useEffect(() => {
    if (selectedCollectionId) dispatch(fetchCollection(selectedCollectionId));
  }, [dispatch, selectedCollectionId]);

  useEffect(() => {
    if (allMulesoftEndpoints) setMulesoftEndpoints(allMulesoftEndpoints);
  }, [allMulesoftEndpoints]);

  useEffect(() => {
    if (selectedDataTable && dataModelId) {
      dispatch(
        fetchAllRecordsFromTable({
          id: dataModelId,
          tableName: selectedDataTable.refName,
          filters: tableDataFilters,
        })
      );
    }
  }, [dispatch, selectedDataTable, dataModelId, tableDataFilters]);

  useEffect(() => {
    if (appId && !selectedApp)
      dispatch(fetchApplicationById(appId)).then((res) => {
        if (!res.payload.dataModelId) return;
        dispatch(fetchDataModel(res.payload.dataModelId));
      });
  }, [dispatch, appId, selectedApp]);

  useEffect(() => {
    if (selectRecentApp) {
      const { appRefName, id, dataModelId, cloudService } = selectRecentApp;
      dispatch(setAppDeployStatus());
      dispatch(
        setAppPipelineStatus({
          frontend: FRONTEND_INITIAL_STATUS,
          backend: BACKEND_INITIAL_STATUS,
        })
      );
      dispatch(
        fetchApplicationDeploymentStatus({
          repoName: appRefName,
          appId: id,
          dataModelId,
          cloudService,
        })
      ).then((res) => {
        if (!res.payload) {
          dispatch(
            setAppPipelineStatus({
              frontend: FRONTEND_FAILURE_STATUS,
              backend: BACKEND_FAILURE_STATUS,
            })
          );
          dispatch(
            setAppDeployStatus({
              frontend: FAILURE,
              backend: FAILURE,
            })
          );
        } else {
          const frontendStatus = getDeploymentStatus(res.payload?.frontend);
          const backendStatus = getDeploymentStatus(res.payload?.backend);
          dispatch(
            setAppDeployStatus({
              frontend: frontendStatus,
              backend: backendStatus,
            })
          );
        }
      });
    } else
      dispatch(
        setAppPipelineStatus({
          backend: BACKEND_INITIAL_STATUS,
          frontend: FRONTEND_INITIAL_STATUS,
        })
      );
  }, [selectRecentApp, dispatch]);

  const handleRefresh = useCallback(() => {
    if (!selectRecentApp?.appRefName) return;
    const { id, appRefName, dataModelId, cloudService } = selectRecentApp;
    dispatch(
      fetchApplicationDeploymentStatus({
        repoName: appRefName,
        appId: id,
        dataModelId,
        cloudService,
      })
    ).then((res) => {
      if (res.payload) {
        const frontendStatus = getDeploymentStatus(res.payload.frontend);
        const backendStatus = getDeploymentStatus(res.payload.backend);

        if (
          [SUCCESS, FAILURE, REPO_NOT_FOUND].includes(frontendStatus) &&
          [SUCCESS, FAILURE, REPO_NOT_FOUND].includes(backendStatus)
        ) {
          if (frontendStatus === SUCCESS && backendStatus === SUCCESS) {
            const updatedDeploymentsTime = {
              ...appDeploymentTime,
              [id]: Date.now(),
            };
            localStorage.setItem(
              APP_DEPLOY_AT,
              JSON.stringify(updatedDeploymentsTime)
            );
          }
          dispatch(
            setAppDeployStatus({
              frontend: frontendStatus,
              backend: backendStatus,
            })
          );
          clearInterval(pipelineStatusInterval.current);
        }
      } else {
        dispatch(
          setAppPipelineStatus({
            frontend: FRONTEND_FAILURE_STATUS,
            backend: BACKEND_FAILURE_STATUS,
          })
        );
        dispatch(
          setAppDeployStatus({
            frontend: FAILURE,
            backend: FAILURE,
          })
        );
      }
    });
  }, [selectRecentApp, dispatch, appDeploymentTime]);

  useEffect(() => {
    if (isDeployStatusInProgress) {
      pipelineStatusInterval.current = setInterval(
        handleRefresh,
        REFRESH_FRONTEND_DEPLOY_STATUS
      );
    }
    return () => clearInterval(pipelineStatusInterval.current);
  }, [handleRefresh, isDeployStatusInProgress]);

  useEffect(() => {
    if (selectedDataModelResources) {
      const { collections, endpoints, services, awsResources } =
        selectedDataModelResources;
      dispatch(
        setAreResourcesPending(
          collections === PENDING ||
            endpoints === PENDING ||
            services === PENDING ||
            awsResources === PENDING
        )
      );
    }
  }, [selectedDataModelResources, dispatch]);

  useEffect(() => {
    if (dataModelId) {
      dispatch(
        fetchConstantsFromLibrary({ type: LIBRARY_CONSTANTS.COLLECTIONS })
      );
      dispatch(
        fetchConstantsFromLibrary({
          type: LIBRARY_CONSTANTS.COLLECTION_PROPERTIES,
        })
      );
    }
  }, [dispatch, dataModelId]);

  useEffect(() => {
    let dataFetchInterval;

    const fetchData = async () => {
      if (areResourcesPending && dataModelId) {
        dispatch(fetchDataModel(dataModelId)).then((res) => {
          if (!res?.payload) return;
          dispatch(setDataModelResources(res.payload.dataModelResources));
          dispatch(setDataModelWithPendingResources(res.payload));
        });
      }
    };

    const handleDataModelResponse = async (response) => {
      if (!response?.payload) return;

      const { cloudService, dbRefName, dataModelResources } = response.payload;

      dispatch(setDeployStatus());
      dispatch(setPipelineStatus());
      dispatch(setDataModelResources(dataModelResources));

      dispatch(
        fetchPipelineStatus({
          repoName: dbRefName,
          dataModelId,
          cloudService,
        })
      ).then((res) => {
        if (res.payload) {
          const status = getDeploymentStatus(res.payload.backend);
          dispatch(setDeployStatus(status));
        }
      });
    };

    fetchData();
    dataFetchInterval = setInterval(fetchData, INTERVAL_TIME);

    if (!areResourcesPending && dataModelId) {
      dispatch(setSelectedModal());
      dispatch(fetchDataModel(dataModelId)).then((response) => {
        handleDataModelResponse(response);
      });
    }

    return () => {
      clearInterval(dataFetchInterval);
    };
  }, [dataModelId, dispatch, areResourcesPending, setSelectedModal]);

  useEffect(() => {
    setApplicationsCount(totalApplications);
  }, [totalApplications]);

  useEffect(() => {
    setDataModelsCount(totalDataModels);
  }, [totalDataModels]);

  useEffect(() => {
    totalUsers && setUsersCount(totalUsers);
  }, [totalUsers]);

  useEffect(() => {
    setCollectionsCount(totalCollections);
  }, [totalCollections]);

  useEffect(() => {
    setBackgroundTasksCount(totalBackgroundTasks);
  }, [totalBackgroundTasks]);

  useEffect(() => {
    setEndpointsCount(totalEndpoints);
  }, [totalEndpoints]);

  useEffect(() => {
    setServicesCount(totalServices);
  }, [totalServices]);

  useEffect(() => {
    const { total } = muleIntegrations;
    setTotalMulesoftProjects(total);
  }, [muleIntegrations]);

  useEffect(() => {
    if (currentUser) dispatch(fetchApplications(applicationFilters));
  }, [currentUser, dispatch, applicationFilters]);

  useEffect(() => {
    if (currentUser) dispatch(fetchDataModels(dataModelsFilters));
  }, [dispatch, currentUser, dataModelsFilters]);

  useEffect(() => {
    if (currentUser)
      dispatch(
        fetchConstantsFromLibrary({
          type: LIBRARY_CONSTANTS.BACKEND_TEMPLATES,
        })
      );
  }, [dispatch, currentUser]);

  useEffect(() => {
    if (selectedOrganizationUsersId)
      dispatch(
        fetchOrganizationUsersById({
          id: selectedOrganizationUsersId,
          filters: organizationUsersFilters,
        })
      );
  }, [dispatch, selectedOrganizationUsersId, organizationUsersFilters]);

  useEffect(() => {
    if (currentUser) dispatch(fetchMuleIntegrations(muleIntegrationsFilters));
  }, [dispatch, currentUser, muleIntegrationsFilters]);

  useEffect(() => {
    if (currentUser) dispatch(fetchUsers(usersFilters));
  }, [currentUser, dispatch, usersFilters]);

  useEffect(() => {
    if (integrationId) dispatch(fetchMulesoftEndpoints({ id: integrationId }));
    dispatch(fetchConstantsFromLibrary({ type: LIBRARY_CONSTANTS.SERVICES }));
  }, [currentUser, dispatch, integrationId]);

  useEffect(() => {
    if (appId && shouldFetchDeploymentLogs(deployStatus?.frontend))
      dispatch(fetchApplicationsDeploymentsTrack(appId));
  }, [dispatch, deployStatus?.frontend, appId]);

  useEffect(() => {
    if (dataModelId && !areResourcesPending)
      dispatch(
        fetchCollections({
          id: dataModelId,
          filters: collectionFilters,
        })
      );
  }, [dataModelId, dispatch, collectionFilters, areResourcesPending]);

  useEffect(() => {
    if (dataModelId && !areResourcesPending)
      dispatch(fetchENVSecrets(dataModelId));
  }, [dataModelId, dispatch, areResourcesPending]);

  useEffect(() => {
    if (
      dataModelId &&
      shouldFetchDeploymentLogs(dataModelDeployStatus) &&
      !areResourcesPending
    )
      dispatch(fetchDataModelDeploymentsTrack(dataModelId));
  }, [dataModelDeployStatus, dispatch, dataModelId, areResourcesPending]);

  useEffect(() => {
    if (dataModelId && !areResourcesPending) {
      dispatch(
        fetchBackgroundTasks({
          id: dataModelId,
          filters: backgroundTaskFilters,
        })
      );
    }
  }, [dataModelId, dispatch, backgroundTaskFilters, areResourcesPending]);

  useEffect(() => {
    if (dataModelId && !areResourcesPending) {
      dispatch(
        fetchEndpoints({
          id: dataModelId,
          filters: endpointsFilters,
        })
      );
    }
  }, [dataModelId, dispatch, endpointsFilters, areResourcesPending]);

  useEffect(() => {
    if (dataModelId && !areResourcesPending) {
      dispatch(
        fetchServices({
          id: dataModelId,
          filters: servicesFilters,
        })
      );
    }
  }, [dataModelId, dispatch, servicesFilters, areResourcesPending]);

  useEffect(() => {
    if (dataModelId && dataModelDeployStatus === SUCCESS && endpoints)
      dispatch(fetchSwaggerData({ id: dataModelId }));
  }, [
    dataModelId,
    dispatch,
    recentDeployedEndpointInfo,
    endpoints,
    dataModelDeployStatus,
  ]);

  useEffect(() => {
    if (
      integrationId &&
      allMulesoftEndpoints &&
      integrationDeployStatus === SUCCESS
    )
      dispatch(fetchMulesoftSwaggerData({ integrationId }));
  }, [
    dispatch,
    integrationId,
    recentDeployedEndpointInfo,
    allMulesoftEndpoints,
    integrationDeployStatus,
  ]);

  useEffect(() => {
    if (selectedServiceId) dispatch(fetchService(selectedServiceId));
  }, [dispatch, selectedServiceId]);

  useEffect(() => {
    if (dataModelId && selectedDataModel && !areResourcesPending)
      dispatch(fetchDataModelRegions());
  }, [dataModelId, dispatch, selectedDataModel, areResourcesPending]);

  useEffect(() => {
    if (currentUser) dispatch(fetchOrganizations(organizationFilters));
  }, [currentUser, dispatch, organizationFilters]);

  useEffect(() => {
    if (integrationId) {
      dispatch(setMulesoftDeployStatus());
      dispatch(fetchIntegration(integrationId)).then((response) => {
        const { integrationRefName, cloudService } = response.payload;
        dispatch(setPipelineStatus());
        dispatch(
          fetchPipelineStatus({
            repoName: integrationRefName,
            integrationId,
            cloudService,
          })
        ).then((res) => {
          if (!res.payload) return;
          const status = getDeploymentStatus(res.payload.backend);
          dispatch(setMulesoftDeployStatus(status));
        });
      });
    }
  }, [dispatch, integrationId]);

  useEffect(() => {
    if (currentUser)
      dispatch(
        fetchConstantsFromLibrary({ type: LIBRARY_CONSTANTS.APPLICATION })
      );
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (organizationId) {
      let updatedInvitationFilters = {
        ...invitationFilters,
        organizationId,
      };
      dispatch(fetchInvitations(updatedInvitationFilters));
    }
  }, [dispatch, invitationFilters, organizationId]);

  const applicationSubMenuOptions = [
    {
      id: uuidv4(),
      name: "Database",
      path: "app-data-modal",
      image: dbSidePanelIcon,
      shouldShow: showAppDataModal,
    },
    {
      id: uuidv4(),
      name: "Deployments",
      path: "deployments",
      image: deployIcon,
      shouldShow: showApplicationDeploymentsTrack,
    },
    {
      id: uuidv4(),
      name: "Settings",
      path: "settings",
      image: serverIcon,
      shouldShow: showApplicationSettings,
    },
  ];

  const dataModelSubMenuOptions = [
    {
      id: uuidv4(),
      name: "Collections",
      path: "collections",
      count: collectionsCount.toString(),
      image: menuAltIcon,
      shouldShow: showCollections,
    },
    {
      id: uuidv4(),
      name: "Endpoints",
      path: "endpoints",
      count: endpointsCount.toString(),
      image: endpointIcon,
      shouldShow: showEndpoints,
    },
    {
      id: uuidv4(),
      name: "Services",
      path: "services",
      count: servicesCount.toString(),
      image: serviceIcon,
      shouldShow: showServices,
    },
    {
      id: uuidv4(),
      name: "Background Task",
      path: "background-tasks",
      count: backgroundTasksCount.toString(),
      image: reloadIcon,
      shouldShow: showBackgroundTasks,
    },
    {
      id: uuidv4(),
      name: "Query Tool",
      path: "queryTool",
      image: queryToolIcon,
      shouldShow: showQueryTool,
    },
    {
      id: uuidv4(),
      name: "API Docs",
      path: "api-docs",
      image: hashTagIcon,
      shouldShow: showApiDocs,
    },
    {
      id: uuidv4(),
      name: "Data",
      path: "data",
      image: dbSidePanelIcon,
      shouldShow: showData,
    },
    {
      id: uuidv4(),
      name: "Deployments",
      path: "deployments",
      image: deployIcon,
      shouldShow: showDataModelDeploymentsTrack,
    },
    {
      id: uuidv4(),
      name: "Settings",
      path: "settings",
      image: serverIcon,
      shouldShow: showDatamodelSettings,
    },
  ];

  const mulesoftSubMenuOptions = [
    {
      id: uuidv4(),
      name: "Endpoints",
      path: "endpoints",
      count: mulesoftEndpoints.length.toString(),
      image: endpointIcon,
      shouldShow: showEndpoints,
    },
    {
      id: uuidv4(),
      name: "Services",
      path: "services",
      count: allMulesoftServices.length.toString(),
      image: serviceIcon,
      shouldShow: showServices,
    },
    {
      id: uuidv4(),
      name: "API Docs",
      path: "api-docs",
      image: hashTagIcon,
      shouldShow: showApiDocs,
    },
    {
      id: uuidv4(),
      name: "Generate Endpoints from XML",
      path: "generate",
      image: reloadIcon,
      shouldShow: showMulesoftGeneratedEndpoints,
    },
  ];

  const menuOptions = [
    {
      id: uuidv4(),
      name: "Applications",
      path: "applications",
      count: applicationsCount.toString(),
      image: folderIcon,
      child: applicationSubMenuOptions.filter((option) => option.shouldShow),
      shouldShow: showApplications,
    },
    {
      id: uuidv4(),
      name: "Data Models",
      path: "data-models",
      count: dataModelsCount.toString(),
      image: dbSidePanelIcon,
      child: dataModelSubMenuOptions.filter((option) => option.shouldShow),
      shouldShow: showDataModels,
    },
    {
      id: uuidv4(),
      name: "Mulesoft",
      path: "mulesoft",
      image: appLightIcon,
      count: totalMulesoftProjects?.toString(),
      child: mulesoftSubMenuOptions.filter((option) => option.shouldShow),
      shouldShow: showIntegrations,
    },
    {
      id: uuidv4(),
      name: "Users",
      path: "users",
      count: usersCount.toString(),
      image: usersIcon,
      child: [],
      shouldShow: showUsers,
    },
  ];
  const filteredMenuOptions = menuOptions.filter((option) => option.shouldShow);
  return (
    <>
      {shouldShowLeftPane && <Menubar options={filteredMenuOptions} />}
      {shouldShowDeploymentLogModal && <DeploymentLogsModal />}
      {shouldShowModal && renderModal(selectedModal)}
      {shouldShowDrawer && renderDrawer(selectedDrawer)}
    </>
  );
};

export default SidePanel;
